import { Menu } from 'lucide-react';
import { UserMenu } from './UserMenu';
import { SearchBar } from './SearchBar';
import type { UnifiedTopbarProps } from './Layout.types';

export type { UnifiedTopbarProps } from './Layout.types';

const TOPBAR_STYLES = {
  default: {
    background:
      'linear-gradient(to top left, rgba(138, 43, 226, 0.12) 0%, rgba(255, 255, 255, 0.95) 60%, rgba(255, 255, 255, 0.98) 100%)',
    glow:
      'radial-gradient(ellipse 80% 120% at left bottom, rgba(138, 43, 226, 0.15) 0%, rgba(138, 43, 226, 0.08) 40%, rgba(255, 255, 255, 0.1) 70%, transparent 100%)',
  },
  auth: {
    background:
      'linear-gradient(to top left, rgba(59, 130, 246, 0.10) 0%, rgba(248, 250, 252, 0.96) 55%, rgba(255, 255, 255, 0.98) 100%)',
    glow:
      'radial-gradient(ellipse 80% 120% at left bottom, rgba(96, 165, 250, 0.14) 0%, rgba(147, 197, 253, 0.06) 40%, rgba(255, 255, 255, 0.08) 70%, transparent 100%)',
  },
} as const;

export function UnifiedTopbar({
  logoText = 'IAMPAM PORTAL',
  entryUrl = '/entry',
  onLogoClick,
  user,
  isAuthenticated = false,
  onLogout,
  onExtendToken,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  topbarExtra,
  variant = 'default',
  isMobile = false,
  onMenuClick,
  userMenuLabels,
}: UnifiedTopbarProps & { isMobile?: boolean; onMenuClick?: () => void }) {
  const tone = TOPBAR_STYLES[variant] ?? TOPBAR_STYLES.default;

  return (
    <header style={{
      height: '56px',
      minHeight: '56px',
      maxHeight: '56px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: isMobile ? 'center' : 'space-between',
      padding: isMobile ? '0 12px' : '0 24px 0 20px',
      borderBottom: '1px solid hsl(220 13% 91%)',
      background: tone.background,
      zIndex: 50,
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      position: 'relative',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
    {/* Ambient glow */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: tone.glow,
          backdropFilter: 'blur(2px)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      {/* Mobile: 햄버거 버튼 (왼쪽) */}
      {isMobile && (
        <button
          onClick={onMenuClick}
          style={{
            position: 'absolute',
            zIndex: 1,
            left: '12px',
            padding: '8px',
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            borderRadius: '6px',
          }}
        >
          <Menu style={{ width: '24px', height: '24px', color: 'hsl(220 9% 30%)' }} />
        </button>
      )}

      {/* 로고 (모바일: 중앙, 데스크탑: 왼쪽) - onLogoClick 있으면 클라이언트 라우팅, 없으면 entryUrl로 전체 이동 */}
      <button
        onClick={() => (onLogoClick ? onLogoClick() : (window.location.href = entryUrl))}
        style={{
          position: 'relative',
          zIndex: 1,
          padding: '6px 12px',
          borderRadius: '6px',
          border: 'none',
          backgroundColor: 'transparent',
          fontSize: isMobile ? '15px' : '16px',
          fontWeight: 700,
          color: 'hsl(220 9% 20%)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          overflow: 'hidden',
          display: 'flex',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'hsl(221 83% 53%)';
          e.currentTarget.style.backgroundColor = 'hsl(221 83% 53% / 0.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'hsl(220 9% 20%)';
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span>{logoText}</span>
      </button>

      {/* 오른쪽 영역 */}
      <div style={{
        position: isMobile ? 'absolute' : 'relative',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '8px' : '16px',
        right: isMobile ? '12px' : 'auto',
      }}>
        {!isMobile && onSearchChange && (
          <SearchBar searchValue={searchValue} onSearchChange={onSearchChange} searchPlaceholder={searchPlaceholder} />
        )}
        {topbarExtra}
        {isAuthenticated && (
          <UserMenu user={user} onLogout={onLogout} onExtendToken={onExtendToken} isMobile={isMobile} labels={userMenuLabels} />
        )}
      </div>
    </header>
  );
}
